/* eslint-disable func-names */
import { all, select, takeEvery, put, fork, call } from 'redux-saga/effects';
import { push } from 'redux-first-history';
import { datadogRum } from '@datadog/browser-rum';
import { closeIntercomChat } from '../../helpers/intercomChat';
import { history } from '../store';
import { getAuthData, setLocalStorageAuthData } from '../../helpers/auth';
import { convertCountryContext, getLocalStorageLanguageData } from '../../helpers/countriesHelpers';
import notifications from '../notifications/actions';
import { sendRequest } from '../../helpers/saga';
import { logger } from '../../helpers/logger';
import ApiInstance from '../../helpers/apiInstance';
import Api from '../../helpers/api';
import actions from './actions';
import { setGoogleLanguage, setLanguage, setLanguageByCountry, t } from '../../i18n';
import { loadGoogleMapsScript } from '../../helpers/loadGoogleMaps';
import { steps } from './reducer';

const { REACT_APP_LOGIN_URL: loginUrl } = import.meta.env;
const checkLoginTypeApi = new ApiInstance('users/search');
const checkLoginMethodsApi = new ApiInstance('sessions/form_data');
const api = new Api('sign_in');
const countryUpdateApi = new Api('profile/update_selected_country');
const createPasswordApi = new Api('password');
const checkPasswordTokenApi = new Api('password/new');
const resetPasswordApi = new Api('password/reset');
const confirmationEmailApi = new Api('password/forgot');
const logoutApi = new Api('sign_out');
const logoutFromUnifiedLoginApi = new ApiInstance('logout', '/v1', 'UnifiedLogin');

function* logoutFromUnifiedLoginRequest() {
  const result = yield call(sendRequest, logoutFromUnifiedLoginApi.delete, {}, notifications.REQUEST_ERROR);

  if (result?.status === 200) {
    yield put(actions.logoutFromUnifiedLoginSuccess());
    yield put(push(loginUrl || ''));
  } else {
    yield put(actions.logoutFromUnifiedLoginError());
  }
}

function* checkLoginTypeRequest({ payload }) {
  const { email } = payload;

  const result = yield call(sendRequest, checkLoginTypeApi.post, { data: { email } }, notifications.REQUEST_ERROR);

  if (result?.status === 200) {
    yield put(actions.checkLoginTypeSuccess(result.data));
  } else if (result?.status === 404) {
    yield put(actions.checkLoginTypeError({ error: result?.messages?.[0] }));
  } else {
    yield put(actions.checkLoginTypeError());
    yield put(
      notifications.addNotification({
        headline: t('dash.common.notification.error_headline'),
        type: 'error',
        message: t('dash.common.notification.default_error_message'),
      }),
    );
  }
}

function* checkLoginMethodsRequest() {
  const result = yield call(sendRequest, checkLoginMethodsApi.get, {}, notifications.REQUEST_ERROR);

  if (result?.status === 200) {
    yield put(actions.checkLoginMethodsSuccess(result.data));
  } else {
    yield put(actions.checkLoginMethodsError());
  }
}

export function* loginRequest() {
  yield takeEvery(actions.LOGIN_REQUEST, function* ({ payload }) {
    const { code, fakeProfile, username: email, password } = payload;

    const result = yield call(
      sendRequest,
      api.createItem,
      { data: { email, password } },
      notifications.REQUEST_ERROR,
      null,
      null,
      { ignoreLogout: true },
    );

    if (result?.data) {
      const {
        user: {
          auth_token,
          id,
          time_zone,
          state,
          role,
          intercom,
          avatar_url,
          countries,
          first_name,
          last_name,
          selected_country: selectedCountry,
        },
      } = result.data;
      const avatarUrl = (avatar_url && avatar_url !== 'nophoto' ? avatar_url : '') || fakeProfile?.imageUrl || '';

      yield put({
        type: actions.LOGIN_SUCCESS,
        payload: {
          authToken: auth_token,
          isLoggedIn: !!auth_token,
          id,
          time_zone,
          state,
          role,
          code,
          profile: { name: first_name, lastName: last_name, email, avatar_url: avatarUrl },
          username: email || '',
          intercom,
          countries,
          selectedCountry,
        },
      });
    } else {
      const loginMethods = yield select(state => state.Auth.loginMethods);

      if (loginMethods?.includes('password')) {
        yield put(actions.setStep({ step: steps.LOGIN }));
      } else {
        yield put(actions.setStep({ step: steps.START }));
      }

      yield put({ type: actions.LOGIN_ERROR });
    }
  });
}

export function* loginSuccess() {
  yield takeEvery(actions.LOGIN_SUCCESS, function* ({ payload }) {
    const auth = getAuthData(payload);
    const { id, selectedCountry } = auth;

    datadogRum.setUser({
      id,
      selectedCountry: selectedCountry?.code,
    });

    yield setLocalStorageAuthData(auth);

    const { locale } = auth.selectedCountry;

    const currentLanguage = getLocalStorageLanguageData()?.language;

    if (!currentLanguage) {
      yield setLanguage(locale);
      yield setGoogleLanguage();
    }
  });
}

export function* logout() {
  yield takeEvery(actions.LOGOUT, function* ({ payload }) {
    const features = yield select(state => state.FeatureFlags.flags);
    const isUnifiedLoginEnabled = features?.unified_login_enabled;

    if (!isUnifiedLoginEnabled) {
      const loginMethods = yield select(state => state.Auth.loginMethods);
      const result = yield call(sendRequest, logoutApi.createItem, notifications.REQUEST_ERROR);

      if (result?.status === 200) {
        yield put(actions.logoutSuccess({ ...payload, loginMethods }));
      }
    }

    if (isUnifiedLoginEnabled) {
      yield put(actions.logoutFromUnifiedLogin());
    }

    yield closeIntercomChat();
  });
}

export function* logoutSuccess() {
  yield takeEvery(actions.LOGOUT_SUCCESS, function* ({ payload }) {
    const features = yield select(state => state.FeatureFlags.flags);
    const isUnifiedLoginEnabled = features?.unified_login_enabled;

    yield datadogRum.stopSession();

    if (!isUnifiedLoginEnabled) {
      if (payload?.loginMethods?.length === 1) {
        yield put(actions.setStep({ step: steps.LOGIN }));
      } else {
        yield put(actions.setStep({ step: steps.START }));
      }
    }
  });
}

export function* performCheckAuthorization() {
  const auth = getAuthData();

  if (!auth) return;

  yield put({
    type: actions.LOGIN_SUCCESS,
    payload: auth,
  });
  yield put(actions.checkAuthorizationSuccess());
}

export function* checkAuthorization() {
  yield takeEvery(actions.CHECK_AUTHORIZATION, function* () {
    yield* performCheckAuthorization();
  });
}

export function* checkCreatePasswordToken() {
  yield takeEvery(actions.CHECK_CREATE_PASSWORD_TOKEN_REQUEST, function* (data) {
    const { token } = data.payload;
    const result = yield call(
      sendRequest,
      checkPasswordTokenApi.multipleParamsGetRequest,
      { token },
      notifications.REQUEST_ERROR,
    );

    if (
      result &&
      result.data &&
      result.data.actions &&
      result.data.actions.length &&
      result.data.actions.filter(action => action.key === 'create_password').length
    ) {
      yield put({ type: actions.CHECK_CREATE_PASSWORD_TOKEN_SUCCESS, payload: data.payload });
    } else {
      yield put({ type: actions.CHECK_CREATE_PASSWORD_TOKEN_FAIL });
    }
  });
}

export function* checkResetPasswordToken() {
  yield takeEvery(actions.CHECK_RESET_PASSWORD_TOKEN_REQUEST, function* (data) {
    const { token } = data.payload;
    const result = yield call(
      sendRequest,
      checkPasswordTokenApi.multipleParamsGetRequest,
      { token },
      notifications.REQUEST_ERROR,
    );

    if (
      result &&
      result.data &&
      result.data.actions &&
      result.data.actions.length &&
      result.data.actions.filter(action => action.key === 'reset_password').length
    ) {
      yield put({ type: actions.CHECK_RESET_PASSWORD_TOKEN_SUCCESS, payload: data.payload });
    } else {
      yield put({ type: actions.CHECK_RESET_PASSWORD_TOKEN_FAIL });
    }
  });
}

export function* createPassword() {
  yield takeEvery(actions.CREATE_PASSWORD_REQUEST, function* (data) {
    const { token, password, password_confirmation } = data.payload;
    const result = yield call(
      sendRequest,
      createPasswordApi.createItem,
      { data: { token, password, password_confirmation } },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put({ type: actions.CREATE_PASSWORD_SUCCESS });
    } else {
      yield put({ type: actions.CREATE_PASSWORD_FAIL });
    }
  });
}

export function* resetPassword() {
  yield takeEvery(actions.RESET_PASSWORD_REQUEST, function* (data) {
    const { token, password, password_confirmation } = data.payload;
    const result = yield call(
      sendRequest,
      resetPasswordApi.createItem,
      { data: { token, password, password_confirmation } },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put({ type: actions.RESET_PASSWORD_SUCCESS });
    } else {
      yield put({ type: actions.RESET_PASSWORD_FAIL });
    }
  });
}

export function* sendConfirmationEmail() {
  yield takeEvery(actions.SEND_CONFIRMATION_REQUEST, function* (data) {
    const { email } = data.payload;
    const result = yield call(
      sendRequest,
      confirmationEmailApi.createItem,
      { data: { email } },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put({ type: actions.SEND_CONFIRMATION_SUCCESS });
    } else {
      yield put({ type: actions.SEND_CONFIRMATION_FAIL });
    }
  });
}

function* updateSelectedCountryRequest(data) {
  const { payload } = data;

  try {
    const result = yield call(
      sendRequest,
      countryUpdateApi.updateItem,
      { data: { country_id: payload } },
      notifications.REQUEST_ERROR,
    );

    if (result && result.data) {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.topbar.change_country_success_message'),
        }),
      );
      const availableCountries = yield select(state => state.Auth.countries);
      const selectedCountry = availableCountries && availableCountries.find(country => country.id === payload);

      yield put({
        type: actions.UPDATE_SELECTED_COUNTRY_SUCCESS,
        payload: convertCountryContext(selectedCountry),
      });
      yield setLanguageByCountry(selectedCountry);

      loadGoogleMapsScript();

      yield setGoogleLanguage();

      // Keep the same left nav menu selection after country change
      if (
        (history.location.pathname !== '' && history.location.pathname.split('/').length >= 3) ||
        history.location.search !== ''
      ) {
        yield put(push(`/${history.location.pathname.split('/')[1]}`));
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

export { checkLoginMethodsRequest };

export default function* rootSaga() {
  yield all([
    takeEvery(actions.CHECK_LOGIN_TYPE_REQUEST, checkLoginTypeRequest),
    takeEvery(actions.UPDATE_SELECTED_COUNTRY_REQUEST, updateSelectedCountryRequest),
    takeEvery(actions.LOGOUT_FROM_UNIFIED_LOGIN, logoutFromUnifiedLoginRequest),
    fork(checkCreatePasswordToken),
    fork(checkResetPasswordToken),
    fork(checkAuthorization),
    fork(loginRequest),
    fork(loginSuccess),
    fork(createPassword),
    fork(sendConfirmationEmail),
    fork(resetPassword),
    fork(logout),
    fork(logoutSuccess),
  ]);
}
